import {
	fetchWorldAdm0GeoJson,
	fetchGeoBoundariesManifest,
	fetchLocalGeoBoundariesGeoJson,
	hasLocalGeoBoundariesData,
} from '../../utils/geoBoundariesApi';
import { ServiceArea } from '../../types/serviceArea';
import {
	FETCH_CONCURRENCY,
	HOVER_FILTER_NONE,
	LAYER_ADM0_FILL,
	LAYER_ADM0_LINE,
	LAYER_ADM1_FILL,
	LAYER_ADM1_LINE,
	LAYER_ADM2_FILL,
	LAYER_ADM2_LINE,
	LAYER_ADM3_FILL,
	LAYER_ADM3_LINE,
	LAYER_HOVER_ADM0,
	LAYER_HOVER_ADM1,
	LAYER_HOVER_ADM2,
	LAYER_HOVER_ADM3,
	LAYER_SEL_ADM0_FILL,
	LAYER_SEL_ADM0_LINE,
	LAYER_SEL_ADM1_FILL,
	LAYER_SEL_ADM1_LINE,
	LAYER_SEL_ADM2_FILL,
	LAYER_SEL_ADM2_LINE,
	LAYER_SEL_ADM3_FILL,
	LAYER_SEL_ADM3_LINE,
	LAYER_SEL_MARKERS_DISTRICT,
	LAYER_SEL_MARKERS_REGION,
	LAYER_SEARCH_PREVIEW_LINE,
	LAYER_STORED_FILL,
	LAYER_STORED_LINE,
	PICK_TARGET_FILL_OPACITY,
	SEARCH_PREVIEW_LINE_COLOR,
	SELECTION_FILTER_NONE,
	SEL_COUNTRY_FILL,
	SEL_COUNTRY_LINE,
	SEL_DISTRICT_FILL,
	SEL_DISTRICT_LINE,
	SEL_LOCALITY_FILL,
	SEL_LOCALITY_LINE,
	SEL_REGION_FILL,
	SEL_REGION_LINE,
	SOURCE_ADM0,
	SOURCE_ADM1,
	SOURCE_ADM2,
	SOURCE_ADM3,
	SOURCE_SEARCH_PREVIEW,
	SOURCE_SEL_MARKERS,
	SOURCE_STORED,
	ZOOM_ADM1,
	ZOOM_ADM2,
	ZOOM_ADM3,
} from './progressiveGeoBoundariesMapConstants';
import {
	boundaryFeatureToServiceArea,
	emptyFeatureCollection,
	isBoundaryExplicitlySelectedForFormMode,
	isHoverCoveredByCoarserSelection,
	isFiniteScreenNumber,
	isMapReadyForRenderedFeatureQueries,
	mergeDedupeAdm0,
	normalizeFeatures,
	ProgressiveGeoBoundariesAttachOptions,
	projectMapCenterToScreen,
	resolveBoundaryHoverLineColor,
	resolveBoundaryRefinementFromMapClick,
	resolveIsoFromAdm0FeatureList,
	runPool,
	toMapboxFeatureCollection,
} from './progressiveGeoBoundariesMapCore';

/**
 * Adds geoBoundaries-driven ADM0/ADM1/ADM2 GeoJSON sources and interaction (global dataset: WORLD-ADM0 + per-country ADM1/2).
 * Call only after map "load". Returns a disposer.
 */
export function attachProgressiveGeoBoundariesMapbox(
	map: any,
	options: ProgressiveGeoBoundariesAttachOptions
): () => void {
	const isInteractive = options.interactive !== false;
	map.addSource(SOURCE_ADM0, { type: 'geojson', data: emptyFeatureCollection() });
	map.addSource(SOURCE_ADM1, { type: 'geojson', data: emptyFeatureCollection() });
	map.addSource(SOURCE_ADM2, { type: 'geojson', data: emptyFeatureCollection() });
	map.addSource(SOURCE_ADM3, { type: 'geojson', data: emptyFeatureCollection() });
	map.addSource(SOURCE_STORED, { type: 'geojson', data: emptyFeatureCollection() });
	map.addSource(SOURCE_SEARCH_PREVIEW, { type: 'geojson', data: emptyFeatureCollection() });

	const beforeLayerId = undefined;

	// Near-invisible fills = pick targets for queryRenderedFeatures (opacity 0 is often not queryable).
	map.addLayer(
		{
			id: LAYER_ADM0_FILL,
			type: 'fill',
			source: SOURCE_ADM0,
			paint: {
				'fill-color': '#000000',
				'fill-opacity': PICK_TARGET_FILL_OPACITY,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_ADM0_LINE,
			type: 'line',
			source: SOURCE_ADM0,
			paint: {
				'line-color': '#8c8c8c',
				'line-opacity': 0.45,
				'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.35, 8, 0.9],
			},
		},
		beforeLayerId
	);

	map.addLayer(
		{
			id: LAYER_ADM1_FILL,
			type: 'fill',
			source: SOURCE_ADM1,
			paint: {
				'fill-color': '#000000',
				'fill-opacity': PICK_TARGET_FILL_OPACITY,
			},
			minzoom: ZOOM_ADM1,
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_ADM1_LINE,
			type: 'line',
			source: SOURCE_ADM1,
			paint: {
				'line-color': '#8c8c8c',
				'line-opacity': 0.5,
				'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.3, 10, 0.95],
			},
			minzoom: ZOOM_ADM1,
		},
		beforeLayerId
	);

	map.addLayer(
		{
			id: LAYER_ADM2_FILL,
			type: 'fill',
			source: SOURCE_ADM2,
			paint: {
				'fill-color': '#000000',
				'fill-opacity': PICK_TARGET_FILL_OPACITY,
			},
			minzoom: ZOOM_ADM2,
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_ADM2_LINE,
			type: 'line',
			source: SOURCE_ADM2,
			paint: {
				'line-color': '#8c8c8c',
				'line-opacity': 0.55,
				'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.25, 12, 0.85],
			},
			minzoom: ZOOM_ADM2,
		},
		beforeLayerId
	);

	const selFilterNone = ['==', 'shapeID', SELECTION_FILTER_NONE];
	map.addLayer(
		{
			id: LAYER_SEL_ADM0_FILL,
			type: 'fill',
			source: SOURCE_ADM0,
			filter: selFilterNone,
			paint: {
				'fill-color': SEL_COUNTRY_FILL,
				'fill-opacity': 0.42,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_ADM0_LINE,
			type: 'line',
			source: SOURCE_ADM0,
			filter: selFilterNone,
			paint: {
				'line-color': SEL_COUNTRY_LINE,
				'line-width': 2.4,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_ADM1_FILL,
			type: 'fill',
			source: SOURCE_ADM1,
			filter: selFilterNone,
			paint: {
				'fill-color': SEL_REGION_FILL,
				'fill-opacity': 0.4,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_ADM1_LINE,
			type: 'line',
			source: SOURCE_ADM1,
			filter: selFilterNone,
			paint: {
				'line-color': SEL_REGION_LINE,
				'line-width': 2.1,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_ADM2_FILL,
			type: 'fill',
			source: SOURCE_ADM2,
			filter: selFilterNone,
			paint: {
				'fill-color': SEL_DISTRICT_FILL,
				'fill-opacity': 0.38,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_ADM2_LINE,
			type: 'line',
			source: SOURCE_ADM2,
			filter: selFilterNone,
			paint: {
				'line-color': SEL_DISTRICT_LINE,
				'line-width': 1.9,
			},
		},
		beforeLayerId
	);

	map.addLayer(
		{
			id: LAYER_ADM3_FILL,
			type: 'fill',
			source: SOURCE_ADM3,
			paint: {
				'fill-color': '#000000',
				'fill-opacity': PICK_TARGET_FILL_OPACITY,
			},
			minzoom: ZOOM_ADM3,
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_ADM3_LINE,
			type: 'line',
			source: SOURCE_ADM3,
			paint: {
				'line-color': '#8c8c8c',
				'line-opacity': 0.58,
				'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.22, 14, 0.75],
			},
			minzoom: ZOOM_ADM3,
		},
		beforeLayerId
	);

	map.addLayer(
		{
			id: LAYER_SEL_ADM3_FILL,
			type: 'fill',
			source: SOURCE_ADM3,
			filter: selFilterNone,
			paint: {
				'fill-color': SEL_LOCALITY_FILL,
				'fill-opacity': 0.36,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_ADM3_LINE,
			type: 'line',
			source: SOURCE_ADM3,
			filter: selFilterNone,
			paint: {
				'line-color': SEL_LOCALITY_LINE,
				'line-width': 1.75,
			},
		},
		beforeLayerId
	);

	// Fallback pin markers for selected states/counties when zoomed out
	const createPinImage = (
		fill: string,
		stroke: string
	): { width: number; height: number; data: Uint8Array } => {
		const size = 32;
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		if (!ctx) return { width: size, height: size, data: new Uint8Array(size * size * 4) };
		// Pin shape: teardrop pointing down
		const cx = size / 2;
		const pinTop = 4;
		const circleR = 9;
		const circleY = pinTop + circleR;
		const pinTip = size - 2;
		ctx.beginPath();
		ctx.arc(cx, circleY, circleR, Math.PI * 1.15, Math.PI * -0.15);
		ctx.lineTo(cx, pinTip);
		ctx.closePath();
		ctx.fillStyle = fill;
		ctx.fill();
		ctx.strokeStyle = stroke;
		ctx.lineWidth = 1.5;
		ctx.stroke();
		// Inner dot
		ctx.beginPath();
		ctx.arc(cx, circleY, 3, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		const imageData = ctx.getImageData(0, 0, size, size);
		return { width: size, height: size, data: new Uint8Array(imageData.data.buffer) };
	};

	map.addImage('ow-pin-region', createPinImage(SEL_REGION_FILL, SEL_REGION_LINE));
	map.addImage('ow-pin-district', createPinImage(SEL_DISTRICT_FILL, SEL_DISTRICT_LINE));

	map.addSource(SOURCE_SEL_MARKERS, {
		type: 'geojson',
		data: { type: 'FeatureCollection', features: [] },
	});
	map.addLayer(
		{
			id: LAYER_SEL_MARKERS_REGION,
			type: 'symbol',
			source: SOURCE_SEL_MARKERS,
			filter: ['==', ['get', 'placeType'], 'region'],
			layout: {
				'icon-image': 'ow-pin-region',
				'icon-size': 1,
				'icon-anchor': 'bottom',
				'icon-allow-overlap': true,
				'text-field': ['get', 'name'],
				'text-anchor': 'bottom',
				'text-offset': [0, -2.2],
				'text-size': 12,
				'text-allow-overlap': true,
				'text-ignore-placement': true,
				'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
			},
			paint: {
				'text-color': SEL_REGION_LINE,
				'text-halo-color': '#ffffff',
				'text-halo-width': 1.5,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEL_MARKERS_DISTRICT,
			type: 'symbol',
			source: SOURCE_SEL_MARKERS,
			filter: ['==', ['get', 'placeType'], 'district'],
			// Only show county pins + names when the user is zoomed in enough
			// to actually be looking at counties — avoids label clutter at world/state zoom.
			minzoom: 5,
			layout: {
				'icon-image': 'ow-pin-district',
				'icon-size': 1,
				'icon-anchor': 'bottom',
				'icon-allow-overlap': true,
				'text-field': ['get', 'name'],
				'text-anchor': 'bottom',
				'text-offset': [0, -2.2],
				'text-size': 12,
				'text-allow-overlap': false,
				'text-ignore-placement': false,
				'text-optional': true,
				'text-padding': 4,
				'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
			},
			paint: {
				'text-color': SEL_DISTRICT_LINE,
				'text-halo-color': '#ffffff',
				'text-halo-width': 1.5,
			},
		},
		beforeLayerId
	);

	const addHoverLineLayer = (hoverOutlineLayerId: string, sourceId: string, color: string) => {
		map.addLayer(
			{
				id: hoverOutlineLayerId,
				type: 'line',
				source: sourceId,
				paint: {
					'line-color': color,
					'line-width': 2.5,
				},
				filter: ['==', 'shapeID', HOVER_FILTER_NONE],
			},
			beforeLayerId
		);
	};
	addHoverLineLayer(LAYER_HOVER_ADM0, SOURCE_ADM0, SEL_COUNTRY_LINE);
	addHoverLineLayer(LAYER_HOVER_ADM1, SOURCE_ADM1, SEL_REGION_LINE);
	addHoverLineLayer(LAYER_HOVER_ADM2, SOURCE_ADM2, SEL_DISTRICT_LINE);
	addHoverLineLayer(LAYER_HOVER_ADM3, SOURCE_ADM3, SEL_LOCALITY_LINE);

	function applyDefaultHoverOutlineLineColors() {
		const defaults: [string, string][] = [
			[LAYER_HOVER_ADM0, SEL_COUNTRY_LINE],
			[LAYER_HOVER_ADM1, SEL_REGION_LINE],
			[LAYER_HOVER_ADM2, SEL_DISTRICT_LINE],
			[LAYER_HOVER_ADM3, SEL_LOCALITY_LINE],
		];
		for (let hoverColorIndex = 0; hoverColorIndex < defaults.length; hoverColorIndex++) {
			const layerId = defaults[hoverColorIndex][0];
			const lineColor = defaults[hoverColorIndex][1];
			if (map.getLayer(layerId)) {
				try {
					map.setPaintProperty(layerId, 'line-color', lineColor);
				} catch (_ignoredPaintErr) {}
			}
		}
	}

	// Stored boundaries: rendered from geometry saved at selection time (always visible, no minzoom)
	map.addLayer(
		{
			id: LAYER_STORED_FILL,
			type: 'fill',
			source: SOURCE_STORED,
			paint: {
				'fill-color': [
					'match',
					['get', 'placeType'],
					'country',
					SEL_COUNTRY_FILL,
					'region',
					SEL_REGION_FILL,
					'district',
					SEL_DISTRICT_FILL,
					'place',
					SEL_LOCALITY_FILL,
					SEL_REGION_FILL,
				],
				'fill-opacity': 0.4,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_STORED_LINE,
			type: 'line',
			source: SOURCE_STORED,
			paint: {
				'line-color': [
					'match',
					['get', 'placeType'],
					'country',
					SEL_COUNTRY_LINE,
					'region',
					SEL_REGION_LINE,
					'district',
					SEL_DISTRICT_LINE,
					'place',
					SEL_LOCALITY_LINE,
					SEL_REGION_LINE,
				],
				'line-width': 2,
			},
		},
		beforeLayerId
	);
	map.addLayer(
		{
			id: LAYER_SEARCH_PREVIEW_LINE,
			type: 'line',
			source: SOURCE_SEARCH_PREVIEW,
			paint: {
				'line-color': SEARCH_PREVIEW_LINE_COLOR,
				'line-opacity': 0.95,
				'line-width': 2.5,
			},
		},
		beforeLayerId
	);

	let adm0MergedFeatures: any[] = [];
	let localAdm0BootstrapComplete = false;
	let activeCountryIso: string | null = null;
	let loadingAdm0 = false;
	let loadingAdm1Key: string | null = null;
	let loadingAdm2Key: string | null = null;
	let lastSuccessfulAdm1MergeKey: string | null = null;
	let lastSuccessfulAdm2MergeKey: string | null = null;
	let adm1FetchGeneration = 0;
	let adm2FetchGeneration = 0;
	let lastSuccessfulAdm3Iso: string | null = null;
	let hoverShapeId: string | null = null;
	let disposed = false;

	let debounceTimer: number | null = null;

	const mapHostEl = map.getContainer();
	const boundaryHoverTooltipEl = document.createElement('div');
	// Imperative DOM (no JSX) — still use Tailwind so utilities are scanned and match ServiceAreaMap.
	boundaryHoverTooltipEl.className =
		'ow-progressive-boundary-hover-tooltip pointer-events-none absolute z-30 hidden max-w-[320px] rounded border border-solid border-grey-5 bg-white/[0.96] px-3 py-2 text-[13px] leading-snug text-grey-8 shadow-md';
	boundaryHoverTooltipEl.setAttribute('role', 'tooltip');

	/*
	 * Persistent in-map hint shown at the top-center of the map whenever the user is on the State
	 * or County tab but zoomed out below the level at which those admin polygons are queryable.
	 * Replaces the earlier Ant Design `message` toaster: users asked for a badge bound to the map
	 * that stays visible until the tab changes or they zoom in enough.
	 *
	 * Horizontal centering offsets the pill left of the container center by half of the Select:
	 * Country / State / County legend (~260px wide, pinned at `right-2.5 top-2.5` in
	 * EditSupplierServiceAreasForm). True `left-1/2` centering overlapped that legend and made the
	 * pill feel right-shifted; biasing by ~130px centers it in the free map area to the left of it.
	 */
	const boundaryZoomHintPillEl = document.createElement('div');
	boundaryZoomHintPillEl.className =
		'ow-progressive-boundary-zoom-hint-pill pointer-events-none absolute z-30 left-[calc(50%_-_130px)] top-3 -translate-x-1/2 items-center gap-2 rounded-full border border-solid border-grey-5 bg-white/[0.96] px-3 py-1.5 text-[13px] leading-none text-grey-9 shadow-md';
	boundaryZoomHintPillEl.style.display = 'none';
	boundaryZoomHintPillEl.setAttribute('role', 'status');
	boundaryZoomHintPillEl.setAttribute('aria-live', 'polite');
	const boundaryZoomHintPillIconEl = document.createElement('span');
	boundaryZoomHintPillIconEl.className =
		'ow-progressive-boundary-zoom-hint-pill-icon inline-flex h-[14px] w-[14px] flex-none items-center justify-center rounded-full';
	// Inline SVG info glyph — keeps this non-React module decoupled from Ant Design icons while
	// matching the blue info-circle used by `message.info` previously.
	boundaryZoomHintPillIconEl.innerHTML =
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14" aria-hidden="true" focusable="false"><circle cx="7" cy="7" r="7" fill="#1890FF"></circle><rect x="6.25" y="5.75" width="1.5" height="4.5" rx="0.5" fill="#ffffff"></rect><circle cx="7" cy="4" r="0.9" fill="#ffffff"></circle></svg>';
	const boundaryZoomHintPillTextEl = document.createElement('span');
	boundaryZoomHintPillTextEl.className = 'whitespace-nowrap';
	boundaryZoomHintPillEl.appendChild(boundaryZoomHintPillIconEl);
	boundaryZoomHintPillEl.appendChild(boundaryZoomHintPillTextEl);

	if (mapHostEl) {
		try {
			const computedPosition = window.getComputedStyle(mapHostEl).position;
			if (!computedPosition || computedPosition === 'static') {
				mapHostEl.style.position = 'relative';
			}
		} catch (_ignoredComputedStyleErr) {
			mapHostEl.style.position = 'relative';
		}
		mapHostEl.appendChild(boundaryHoverTooltipEl);
		mapHostEl.appendChild(boundaryZoomHintPillEl);
	}

	/** Last screen point with a visible boundary tooltip — used to refresh Select/Deselect after React updates selections. */
	let lastTooltipScreenPoint: any = null;

	function hideBoundaryHoverTooltip(): void {
		lastTooltipScreenPoint = null;
		boundaryHoverTooltipEl.classList.add('hidden');
		boundaryHoverTooltipEl.textContent = '';
	}

	function setHoverFilter(shapeId: string | null) {
		if (shapeId === hoverShapeId) {
			return;
		}
		hoverShapeId = shapeId;
		const hoverFilterShapeIdToken = shapeId || HOVER_FILTER_NONE;
		const hoverFilter: any = ['==', 'shapeID', hoverFilterShapeIdToken];
		if (map.getLayer(LAYER_HOVER_ADM0)) {
			map.setFilter(LAYER_HOVER_ADM0, hoverFilter);
		}
		if (map.getLayer(LAYER_HOVER_ADM1)) {
			map.setFilter(LAYER_HOVER_ADM1, hoverFilter);
		}
		if (map.getLayer(LAYER_HOVER_ADM2)) {
			map.setFilter(LAYER_HOVER_ADM2, hoverFilter);
		}
		if (map.getLayer(LAYER_HOVER_ADM3)) {
			map.setFilter(LAYER_HOVER_ADM3, hoverFilter);
		}
	}

	function selectionFormMode(): string {
		const modeFromOptions = options.getSelectionFormMode && options.getSelectionFormMode();
		return modeFromOptions ? String(modeFromOptions) : 'district';
	}

	/** Same layer minzoom as ADM1 / ADM2 pick targets — below this, boundaries are not queryable. */
	let attachTrackedSelectionFormMode: string | null = null;

	function zoomTooLowForBoundaryTab(formMode: string, zoomLevel: number): boolean {
		if (formMode === 'region') {
			return zoomLevel < ZOOM_ADM1;
		}
		if (formMode === 'district') {
			return zoomLevel < ZOOM_ADM2;
		}
		return false;
	}

	function resolveZoomHintTextForFormMode(formMode: string, zoomLevel: number): string {
		if (formMode === 'region' && zoomLevel < ZOOM_ADM1) {
			return 'Zoom in to see the states.';
		}
		if (formMode === 'district' && zoomLevel < ZOOM_ADM2) {
			return 'Zoom in to see the counties.';
		}
		return '';
	}

	/**
	 * Single source of truth for the in-map zoom hint pill. Read current form mode + zoom, and
	 * either show the pill (with tab-specific copy) or hide it. Called on every relevant input —
	 * mousemove, click, `zoomend`, and from React on tab change — so the pill always reflects
	 * the live state instead of relying on a toaster throttle.
	 *
	 * Also re-applies the reference admin line visibility when the form mode changes here (from a
	 * map interaction). Tab changes from React still re-apply via `_owApplyBoundaryReferenceLineOpacity`.
	 */
	function updateBoundaryZoomHintPill(): void {
		if (disposed) {
			return;
		}
		const formModeNow = selectionFormMode();
		if (attachTrackedSelectionFormMode !== formModeNow) {
			attachTrackedSelectionFormMode = formModeNow;
			applyReferenceAdminLineVisibilityForFormMode();
		}
		if (options.interactive === false) {
			boundaryZoomHintPillEl.style.display = 'none';
			return;
		}
		const hintText = resolveZoomHintTextForFormMode(formModeNow, map.getZoom());
		if (hintText) {
			boundaryZoomHintPillTextEl.textContent = hintText;
			boundaryZoomHintPillEl.style.display = 'flex';
		} else {
			boundaryZoomHintPillEl.style.display = 'none';
		}
	}

	/**
	 * One admin level per form tab: hit-testing uses only that layer so users cannot pick country
	 * polygons while on State/County (etc.). Layers still respect their own minzoom in the style.
	 */
	function pickBoundaryLayersAtZoom(zoomLevel: number, formMode: string): string[] {
		const resolvedFormMode = formMode || 'district';
		if (resolvedFormMode === 'country') {
			return [LAYER_ADM0_FILL];
		}
		if (resolvedFormMode === 'region') {
			return [LAYER_ADM1_FILL];
		}
		if (resolvedFormMode === 'district') {
			return [LAYER_ADM2_FILL];
		}
		if (resolvedFormMode === 'place') {
			return [LAYER_ADM3_FILL];
		}
		if (zoomLevel >= ZOOM_ADM2) {
			return [LAYER_ADM2_FILL, LAYER_ADM1_FILL, LAYER_ADM0_FILL];
		}
		if (zoomLevel >= ZOOM_ADM1) {
			return [LAYER_ADM1_FILL, LAYER_ADM0_FILL];
		}
		return [LAYER_ADM0_FILL];
	}

	const ADM0_REF_LINE_OPACITY = 0.45;
	const ADM1_REF_LINE_OPACITY = 0.5;
	const ADM2_REF_LINE_OPACITY = 0.55;
	const ADM3_REF_LINE_OPACITY = 0.58;

	// Default line-width interpolations — must mirror the paint specs used when the layers were added
	// (lines 117 / 144 / 172). We need them as data here so we can restore them when the active tab
	// changes (and clears the "parent hint" thicker stroke applied below).
	const ADM0_REF_LINE_WIDTH_DEFAULT: any[] = ['interpolate', ['linear'], ['zoom'], 2, 0.35, 8, 0.9];
	const ADM1_REF_LINE_WIDTH_DEFAULT: any[] = [
		'interpolate',
		['linear'],
		['zoom'],
		3,
		0.3,
		10,
		0.95,
	];
	const ADM2_REF_LINE_WIDTH_DEFAULT: any[] = [
		'interpolate',
		['linear'],
		['zoom'],
		5,
		0.25,
		12,
		0.85,
	];
	// "Parent hint" strokes — slightly thicker than default so users can tell at a glance which state
	// a county is in (on County tab) or which country a state is in (on State tab). Kept conservative
	// (~2× child width at typical viewing zooms) so the reference grid does not fight selection fills.
	const ADM0_REF_LINE_WIDTH_PARENT_HINT: any[] = [
		'interpolate',
		['linear'],
		['zoom'],
		2,
		0.7,
		8,
		1.8,
	];
	const ADM1_REF_LINE_WIDTH_PARENT_HINT: any[] = [
		'interpolate',
		['linear'],
		['zoom'],
		3,
		0.6,
		10,
		1.8,
	];

	/** Composite `admin` / *boundaries* line layers from the basemap (e.g. light-v9), discovered once. */
	const cachedMapboxAdminBoundaryLayerIds: string[] = [];

	/**
	 * Mapbox Light still draws subdivisions from `composite` `admin` (e.g. `admin-3-4-boundaries`).
	 * Those are independent of our `ow-gb-adm*-line` opacities — hide them here so Country tab shows
	 * only geoBoundaries country outlines (and we avoid triple-stacking with our ADM1/2 lines on other tabs).
	 */
	function applyMapboxBasemapAdminBoundaryLineVisibility(): void {
		let style: any;
		try {
			style = map.getStyle();
		} catch (_ignoredGetStyleErr) {
			return;
		}
		if (!style || !style.layers) {
			return;
		}
		if (cachedMapboxAdminBoundaryLayerIds.length === 0) {
			for (let layerScanIndex = 0; layerScanIndex < style.layers.length; layerScanIndex++) {
				const layerSpec = style.layers[layerScanIndex];
				if (layerSpec.type !== 'line') {
					continue;
				}
				if (layerSpec.source !== 'composite') {
					continue;
				}
				if (layerSpec['source-layer'] !== 'admin') {
					continue;
				}
				const layerId = layerSpec.id;
				if (!layerId || layerId.indexOf('boundaries') < 0) {
					continue;
				}
				if (layerId.indexOf('ow-') === 0) {
					continue;
				}
				cachedMapboxAdminBoundaryLayerIds.push(layerId);
			}
		}
		for (let hideIndex = 0; hideIndex < cachedMapboxAdminBoundaryLayerIds.length; hideIndex++) {
			const boundaryLayerId = cachedMapboxAdminBoundaryLayerIds[hideIndex];
			if (!map.getLayer(boundaryLayerId)) {
				continue;
			}
			try {
				map.setLayoutProperty(boundaryLayerId, 'visibility', 'none');
			} catch (_ignoredVisibilityErr) {
				// ignore during style teardown
			}
		}
	}

	function restoreMapboxBasemapAdminBoundaryLineVisibility(): void {
		for (
			let restoreIndex = 0;
			restoreIndex < cachedMapboxAdminBoundaryLayerIds.length;
			restoreIndex++
		) {
			const boundaryLayerId = cachedMapboxAdminBoundaryLayerIds[restoreIndex];
			if (!map.getLayer(boundaryLayerId)) {
				continue;
			}
			try {
				map.setLayoutProperty(boundaryLayerId, 'visibility', 'visible');
			} catch (_ignoredRestoreErr) {
				// ignore
			}
		}
	}

	/**
	 * Gray reference outlines: show the admin level of the active tab plus its immediate parent in a
	 * slightly thicker stroke (State tab → ADM1 + thicker ADM0; County tab → ADM2 + thicker ADM1) so
	 * users can tell at a glance which state/country a hovered county/state belongs to. Other levels
	 * stay hidden so inner grids do not stack on large selections.
	 */
	function applyReferenceAdminLineVisibilityForFormMode(): void {
		const mode = selectionFormMode();
		function setLineOpacity(layerId: string, opacity: number): void {
			if (!map.getLayer(layerId)) {
				return;
			}
			try {
				map.setPaintProperty(layerId, 'line-opacity', opacity);
			} catch (_ignoredLineOpacityErr) {
				// ignore during style teardown
			}
		}
		function setLineWidthExpr(layerId: string, widthExpr: any): void {
			if (!map.getLayer(layerId)) {
				return;
			}
			try {
				map.setPaintProperty(layerId, 'line-width', widthExpr);
			} catch (_ignoredLineWidthErr) {
				// ignore during style teardown
			}
		}
		// Restore defaults first so switching back to (say) State after County drops the parent hint
		// stroke applied for ADM1 on the County tab.
		setLineWidthExpr(LAYER_ADM0_LINE, ADM0_REF_LINE_WIDTH_DEFAULT);
		setLineWidthExpr(LAYER_ADM1_LINE, ADM1_REF_LINE_WIDTH_DEFAULT);
		setLineWidthExpr(LAYER_ADM2_LINE, ADM2_REF_LINE_WIDTH_DEFAULT);
		let adm0Opacity = 0;
		let adm1Opacity = 0;
		let adm2Opacity = 0;
		let adm3Opacity = 0;
		if (mode === 'country') {
			adm0Opacity = ADM0_REF_LINE_OPACITY;
		} else if (mode === 'region') {
			adm1Opacity = ADM1_REF_LINE_OPACITY;
			// Show country borders under states as a thicker hint so "which country is this state in?"
			// is obvious at a glance.
			adm0Opacity = ADM0_REF_LINE_OPACITY;
			setLineWidthExpr(LAYER_ADM0_LINE, ADM0_REF_LINE_WIDTH_PARENT_HINT);
		} else if (mode === 'district') {
			adm2Opacity = ADM2_REF_LINE_OPACITY;
			// Show state borders under counties as a thicker hint so "which state is this county in?"
			// is obvious at a glance (previously only county lines were drawn, which made
			// Nebraska / Kansas interior counties indistinguishable without zooming).
			adm1Opacity = ADM1_REF_LINE_OPACITY;
			setLineWidthExpr(LAYER_ADM1_LINE, ADM1_REF_LINE_WIDTH_PARENT_HINT);
		} else if (mode === 'place') {
			adm3Opacity = ADM3_REF_LINE_OPACITY;
		} else {
			// pin / unknown tab — match prior default (county-weight reference lines)
			adm2Opacity = ADM2_REF_LINE_OPACITY;
		}
		setLineOpacity(LAYER_ADM0_LINE, adm0Opacity);
		setLineOpacity(LAYER_ADM1_LINE, adm1Opacity);
		setLineOpacity(LAYER_ADM2_LINE, adm2Opacity);
		setLineOpacity(LAYER_ADM3_LINE, adm3Opacity);
		applyMapboxBasemapAdminBoundaryLineVisibility();
	}

	/**
	 * Progressive admin layers only for hover/click queries.
	 * Do not include `LAYER_STORED_FILL`: stored selection is stacked above ADM2/ADM1 fills and would
	 * always win `queryRenderedFeatures`, so users could never hit a county under a selected state
	 * (or a state under a selected country) for refinement.
	 */
	function layersForBoundaryInteraction(zoomLevel: number, formMode: string): string[] {
		return pickBoundaryLayersAtZoom(zoomLevel, formMode);
	}

	const queryParentName = (point: any, layerId: string): string | null => {
		if (!map.getLayer(layerId)) {
			return null;
		}
		try {
			const hits = map.queryRenderedFeatures(point, { layers: [layerId] });
			if (hits && hits.length > 0 && hits[0].properties && hits[0].properties.shapeName) {
				return String(hits[0].properties.shapeName);
			}
		} catch (_err) {
			// ignore
		}
		return null;
	};

	const enrichAreaName = (area: ServiceArea, point: any): ServiceArea => {
		if (area.placeType === 'country') {
			return area;
		}
		const parts = [area.name];
		if (area.placeType === 'district') {
			const stateName = queryParentName(point, LAYER_ADM1_FILL);
			if (stateName) {
				parts.push(stateName);
			}
		}
		const countryName = queryParentName(point, LAYER_ADM0_FILL);
		if (countryName) {
			parts.push(countryName);
		}
		return { ...area, name: parts.join(', ') };
	};

	function buildAndShowBoundaryHoverTooltipAtPoint(screenPoint: any, topGeoFeature: any): void {
		if (!mapHostEl || !screenPoint || !topGeoFeature) {
			return;
		}
		lastTooltipScreenPoint = screenPoint;
		const topFeatureProps = topGeoFeature.properties || {};
		const hoveredShapeId = topFeatureProps.shapeID;
		const formModeForTooltip = selectionFormMode();
		const areasForTooltip = options.getServiceAreas ? options.getServiceAreas() : [];
		const hoveredIdForTooltip = hoveredShapeId != null ? String(hoveredShapeId) : '';
		// Tooltip: single admin label only (geoBoundaries `shapeName`). Do not use `enrichAreaName`
		// here — that appends state/country for list UX and made tooltips read "County, State, Country".
		const areaFromHit = boundaryFeatureToServiceArea(topGeoFeature);
		const displayNameForTooltip =
			topFeatureProps.shapeName != null && String(topFeatureProps.shapeName) !== ''
				? String(topFeatureProps.shapeName)
				: areaFromHit.name || 'Unknown';
		const onSelectForTooltip = options.getOnSelect ? options.getOnSelect() : undefined;
		const showSelectDeselectCopy =
			isInteractive && typeof onSelectForTooltip === 'function' && hoveredIdForTooltip !== '';
		let tooltipText = displayNameForTooltip;
		if (showSelectDeselectCopy) {
			const getStateLabelForTip = options.getStateLabelFromArea;
			const getCountryLabelForTip = options.getCountryLabelFromArea;
			let coveredByCoarserSelection = false;
			if (getStateLabelForTip && getCountryLabelForTip) {
				const shapeTypeUpperForCoverage = String((topFeatureProps.shapeType || '').toUpperCase());
				const enrichedForCoverage = enrichAreaName(
					boundaryFeatureToServiceArea(topGeoFeature),
					screenPoint
				);
				coveredByCoarserSelection = isHoverCoveredByCoarserSelection(
					formModeForTooltip,
					shapeTypeUpperForCoverage,
					enrichedForCoverage,
					areasForTooltip,
					getStateLabelForTip,
					getCountryLabelForTip
				);
			}
			const explicitlySelected = isBoundaryExplicitlySelectedForFormMode(
				formModeForTooltip,
				hoveredIdForTooltip,
				areasForTooltip
			);
			const verb = explicitlySelected || coveredByCoarserSelection ? 'Deselect' : 'Select';
			tooltipText = verb + ': ' + displayNameForTooltip;
		}
		boundaryHoverTooltipEl.textContent = tooltipText;
		boundaryHoverTooltipEl.classList.remove('hidden');
		const padPx = 14;
		let leftPx = screenPoint.x + padPx;
		let topPx = screenPoint.y + padPx;
		boundaryHoverTooltipEl.style.left = leftPx + 'px';
		boundaryHoverTooltipEl.style.top = topPx + 'px';
		const maxLeftPx = mapHostEl.clientWidth - boundaryHoverTooltipEl.offsetWidth - 8;
		const maxTopPx = mapHostEl.clientHeight - boundaryHoverTooltipEl.offsetHeight - 8;
		if (leftPx > maxLeftPx) {
			leftPx = Math.max(8, maxLeftPx);
		}
		if (topPx > maxTopPx) {
			topPx = Math.max(8, maxTopPx);
		}
		boundaryHoverTooltipEl.style.left = leftPx + 'px';
		boundaryHoverTooltipEl.style.top = topPx + 'px';
	}

	function refreshBoundaryHoverTooltipFromLastPointer(): void {
		if (!mapHostEl || !lastTooltipScreenPoint || disposed) {
			return;
		}
		if (!isMapReadyForRenderedFeatureQueries(map)) {
			return;
		}
		const interactionMode = options.getInteractionMode
			? options.getInteractionMode()
			: 'boundaries';
		if (interactionMode === 'pin_drop') {
			return;
		}
		const currentZoom = map.getZoom();
		const layers = layersForBoundaryInteraction(currentZoom, selectionFormMode());
		let featsUnderPointer: any[];
		try {
			featsUnderPointer = map.queryRenderedFeatures(lastTooltipScreenPoint, { layers: layers });
		} catch (_err) {
			return;
		}
		if (featsUnderPointer.length > 0) {
			buildAndShowBoundaryHoverTooltipAtPoint(lastTooltipScreenPoint, featsUnderPointer[0]);
		} else {
			hideBoundaryHoverTooltip();
		}
	}

	function scheduleBoundaryTooltipRefreshAfterStateUpdate(screenPoint: any): void {
		window.setTimeout(function () {
			if (disposed) {
				return;
			}
			if (screenPoint) {
				lastTooltipScreenPoint = screenPoint;
			}
			refreshBoundaryHoverTooltipFromLastPointer();
		}, 0);
	}

	function onMouseMove(mousemoveEvent: any) {
		const interactionMode = options.getInteractionMode
			? options.getInteractionMode()
			: 'boundaries';
		if (interactionMode === 'pin_drop') {
			map.getCanvas().style.cursor = 'crosshair';
			setHoverFilter(null);
			applyDefaultHoverOutlineLineColors();
			hideBoundaryHoverTooltip();
			return;
		}
		updateBoundaryZoomHintPill();
		if (!mousemoveEvent || !mousemoveEvent.point || !isMapReadyForRenderedFeatureQueries(map)) {
			map.getCanvas().style.cursor = '';
			hideBoundaryHoverTooltip();
			return;
		}
		const currentZoom = map.getZoom();
		const layers = layersForBoundaryInteraction(currentZoom, selectionFormMode());
		let featuresUnderPointer: any[];
		try {
			featuresUnderPointer = map.queryRenderedFeatures(mousemoveEvent.point, { layers: layers });
		} catch (_err) {
			map.getCanvas().style.cursor = '';
			setHoverFilter(null);
			applyDefaultHoverOutlineLineColors();
			hideBoundaryHoverTooltip();
			return;
		}
		if (featuresUnderPointer.length > 0) {
			const topFeatureProps = featuresUnderPointer[0].properties || {};
			const hoveredShapeId = topFeatureProps.shapeID;
			setHoverFilter(hoveredShapeId != null ? String(hoveredShapeId) : null);
			map.getCanvas().style.cursor = 'pointer';
			applyDefaultHoverOutlineLineColors();
			const shapeTypeUpper = String(topFeatureProps.shapeType || '').toUpperCase();
			const getAreas = options.getServiceAreas;
			const areasList = getAreas ? getAreas() : [];
			const getStateLabel = options.getStateLabelFromArea;
			const getCountryLabel = options.getCountryLabelFromArea;
			if (areasList.length > 0 && getStateLabel && getCountryLabel) {
				const enrichedBoundary = enrichAreaName(
					boundaryFeatureToServiceArea(featuresUnderPointer[0]),
					mousemoveEvent.point
				);
				const hoverLineColor = resolveBoundaryHoverLineColor(
					shapeTypeUpper,
					enrichedBoundary,
					areasList,
					getStateLabel,
					getCountryLabel
				);
				let hoverLayerForColor: string | null = null;
				if (shapeTypeUpper === 'ADM0') {
					hoverLayerForColor = LAYER_HOVER_ADM0;
				} else if (shapeTypeUpper === 'ADM1') {
					hoverLayerForColor = LAYER_HOVER_ADM1;
				} else if (shapeTypeUpper === 'ADM2') {
					hoverLayerForColor = LAYER_HOVER_ADM2;
				} else if (shapeTypeUpper === 'ADM3') {
					hoverLayerForColor = LAYER_HOVER_ADM3;
				}
				if (hoverLayerForColor && map.getLayer(hoverLayerForColor)) {
					try {
						map.setPaintProperty(hoverLayerForColor, 'line-color', hoverLineColor);
					} catch (_ignoredPaintErr2) {}
				}
			}
			if (mapHostEl) {
				buildAndShowBoundaryHoverTooltipAtPoint(mousemoveEvent.point, featuresUnderPointer[0]);
			}
		} else {
			map.getCanvas().style.cursor = '';
			setHoverFilter(null);
			applyDefaultHoverOutlineLineColors();
			hideBoundaryHoverTooltip();
			// Pill was refreshed at the top of onMouseMove — nothing extra to do here now that
			// the hint is persistent (no throttled re-show needed).
		}
	}

	function onMouseLeave() {
		map.getCanvas().style.cursor = '';
		setHoverFilter(null);
		applyDefaultHoverOutlineLineColors();
		hideBoundaryHoverTooltip();
	}

	const onClick = (clickEvent: any) => {
		const interactionMode = options.getInteractionMode
			? options.getInteractionMode()
			: 'boundaries';
		if (interactionMode === 'pin_drop') {
			const onPinSelectHandler = options.getOnPinSelect ? options.getOnPinSelect() : undefined;
			if (onPinSelectHandler) {
				onPinSelectHandler(clickEvent.lngLat);
			}
			return;
		}
		updateBoundaryZoomHintPill();
		if (!clickEvent || !clickEvent.point || !isMapReadyForRenderedFeatureQueries(map)) {
			return;
		}
		const clickZoom = map.getZoom();
		const formModeForClick = selectionFormMode();
		if (zoomTooLowForBoundaryTab(formModeForClick, clickZoom)) {
			// Pill is already visible from the updateBoundaryZoomHintPill call above — just bail.
			return;
		}
		const layers = layersForBoundaryInteraction(clickZoom, formModeForClick);
		let clickedFeatures: any[];
		try {
			clickedFeatures = map.queryRenderedFeatures(clickEvent.point, { layers: layers });
		} catch (_err) {
			return;
		}
		if (clickedFeatures.length === 0) {
			return;
		}
		const topClickedFeature = clickedFeatures[0];
		const clickedFeatureProps = topClickedFeature.properties || {};
		if (clickedFeatureProps.shapeGroup) {
			activeCountryIso = String(clickedFeatureProps.shapeGroup);
		}
		const getAreasForClick = options.getServiceAreas ? options.getServiceAreas : undefined;
		const refinementHandler =
			options.getOnApplyBoundaryRefinement && options.getOnApplyBoundaryRefinement();
		if (
			refinementHandler &&
			getAreasForClick &&
			options.getStateLabelFromArea &&
			options.getCountryLabelFromArea
		) {
			const enrichedChild = enrichAreaName(
				boundaryFeatureToServiceArea(topClickedFeature),
				clickEvent.point
			);
			const refinement = resolveBoundaryRefinementFromMapClick(
				String(clickedFeatureProps.shapeType || '').toUpperCase(),
				selectionFormMode(),
				enrichedChild,
				getAreasForClick(),
				options.getStateLabelFromArea,
				options.getCountryLabelFromArea
			);
			if (refinement) {
				refinementHandler(refinement);
				scheduleRefresh();
				scheduleBoundaryTooltipRefreshAfterStateUpdate(clickEvent.point);
				return;
			}
		}
		const onBoundarySelectHandler = options.getOnSelect ? options.getOnSelect() : undefined;
		if (onBoundarySelectHandler) {
			const enrichedArea = boundaryFeatureToServiceArea(topClickedFeature);
			onBoundarySelectHandler(enrichAreaName(enrichedArea, clickEvent.point));
		}
		scheduleRefresh();
		scheduleBoundaryTooltipRefreshAfterStateUpdate(clickEvent.point);
	};

	function notifyLoading(loading: boolean, level: string) {
		if (options.onBoundaryLoadingChange) {
			options.onBoundaryLoadingChange(loading, level);
		}
	}

	async function loadLocalAdm0Countries() {
		if (loadingAdm0 || disposed || localAdm0BootstrapComplete) {
			return;
		}
		loadingAdm0 = true;
		notifyLoading(true, 'ADM0');
		try {
			let mergedAdm0Features: any[] = [];
			try {
				const worldAdm0FeatureCollection = await fetchWorldAdm0GeoJson();
				if (
					worldAdm0FeatureCollection &&
					worldAdm0FeatureCollection.features &&
					worldAdm0FeatureCollection.features.length > 0
				) {
					mergedAdm0Features = normalizeFeatures(worldAdm0FeatureCollection);
				}
			} catch (worldErr) {
				console.warn(
					'WORLD-ADM0 load failed, falling back to manifest + per-country ADM0',
					worldErr
				);
				const geoBoundariesManifest = await fetchGeoBoundariesManifest();
				const countryIsoCodes =
					geoBoundariesManifest &&
					geoBoundariesManifest.isos &&
					geoBoundariesManifest.isos.length > 0
						? geoBoundariesManifest.isos.slice()
						: [];
				const manifestAdm0Features: any[] = [];
				await runPool(countryIsoCodes, FETCH_CONCURRENCY, async (iso: string) => {
					try {
						const perCountryAdm0Fc = await fetchLocalGeoBoundariesGeoJson(iso, 'ADM0');
						const perCountryNormalized = normalizeFeatures(perCountryAdm0Fc);
						manifestAdm0Features.push.apply(manifestAdm0Features, perCountryNormalized);
					} catch (perCountryErr) {
						console.warn('local ADM0 load failed', iso, perCountryErr);
					}
				});
				mergedAdm0Features = manifestAdm0Features;
			}
			if (!disposed && mergedAdm0Features.length > 0) {
				const adm0MapSource = map.getSource(SOURCE_ADM0);
				adm0MergedFeatures = mergeDedupeAdm0([], mergedAdm0Features);
				adm0MapSource.setData({ type: 'FeatureCollection', features: adm0MergedFeatures });
				scheduleRefresh();
				if (map.once && typeof map.once === 'function') {
					map.once('idle', scheduleRefresh);
				}
			}
		} finally {
			loadingAdm0 = false;
			localAdm0BootstrapComplete = true;
			notifyLoading(false, 'ADM0');
		}
	}

	/**
	 * Which ISO3 has ADM1/ADM2 loaded. Uses several screen samples around the map center so a single
	 * miss (labels, gaps, antimeridian) does not keep a stale country from before the pan (wrong ADM1/2).
	 */
	function resolveActiveCountryIso(): string | null {
		const centerPt = projectMapCenterToScreen(map);
		if (!centerPt) {
			return null;
		}
		if (!map.getLayer(LAYER_ADM0_FILL)) {
			return null;
		}
		const offsets = [
			[0, 0],
			[0, -28],
			[0, 28],
			[-28, 0],
			[28, 0],
			[-22, -22],
			[22, -22],
			[-22, 22],
			[22, 22],
		];
		for (let sampleOffsetIndex = 0; sampleOffsetIndex < offsets.length; sampleOffsetIndex++) {
			const sampleScreenX = centerPt.x + offsets[sampleOffsetIndex][0];
			const sampleScreenY = centerPt.y + offsets[sampleOffsetIndex][1];
			if (!isFiniteScreenNumber(sampleScreenX) || !isFiniteScreenNumber(sampleScreenY)) {
				continue;
			}
			let centerSampleHits: any[];
			try {
				centerSampleHits = map.queryRenderedFeatures(
					{ x: sampleScreenX, y: sampleScreenY },
					{ layers: [LAYER_ADM0_FILL] }
				);
			} catch (_err) {
				continue;
			}
			if (centerSampleHits && centerSampleHits.length > 0 && centerSampleHits[0].properties) {
				const countryIsoFromHit =
					centerSampleHits[0].properties.shapeGroup || centerSampleHits[0].properties.shapeISO;
				if (countryIsoFromHit) {
					return String(countryIsoFromHit);
				}
			}
		}
		const mapCenterLngLat = map.getCenter();
		const lng = typeof mapCenterLngLat.lng === 'number' ? mapCenterLngLat.lng : mapCenterLngLat[0];
		const lat = typeof mapCenterLngLat.lat === 'number' ? mapCenterLngLat.lat : mapCenterLngLat[1];
		if (isFiniteScreenNumber(lng) && isFiniteScreenNumber(lat) && adm0MergedFeatures.length > 0) {
			const fromMem = resolveIsoFromAdm0FeatureList(lng, lat, adm0MergedFeatures);
			if (fromMem) {
				return fromMem;
			}
		}
		return null;
	}

	function dedupeIsoStringsForMerge(isoStrings: string[]): string[] {
		const seenIsoCodes: { [key: string]: boolean } = {};
		const uniqueIsoCodes: string[] = [];
		for (let stringIndex = 0; stringIndex < isoStrings.length; stringIndex++) {
			const isoCode = isoStrings[stringIndex];
			if (!isoCode || seenIsoCodes[isoCode]) {
				continue;
			}
			seenIsoCodes[isoCode] = true;
			uniqueIsoCodes.push(isoCode);
		}
		return uniqueIsoCodes;
	}

	/**
	 * geoBoundaries ADM1/ADM2 are one file per country. The viewport alone would swap the source when
	 * panning (e.g. CAN under the cursor), which drops US states from the GeoJSON and breaks purple
	 * highlights for a selected United States. Merge viewport ISO with every selection row's
	 * boundaryShapeGroup (country / state / county parent).
	 */
	function collectCountryShapeGroupsForMergedAdminLayers(): string[] {
		if (!options.getServiceAreas) {
			return [];
		}
		const selectedServiceAreas = options.getServiceAreas();
		if (!selectedServiceAreas || selectedServiceAreas.length === 0) {
			return [];
		}
		const parentCountryIsoCodes: string[] = [];
		for (let selectionIndex = 0; selectionIndex < selectedServiceAreas.length; selectionIndex++) {
			const selectedArea = selectedServiceAreas[selectionIndex];
			if (!selectedArea || !selectedArea.boundaryShapeGroup) {
				continue;
			}
			const parentCountryIso = String(selectedArea.boundaryShapeGroup).trim();
			if (!parentCountryIso) {
				continue;
			}
			const placeType = selectedArea.placeType;
			if (
				placeType === 'country' ||
				placeType === 'region' ||
				placeType === 'state' ||
				placeType === 'district'
			) {
				parentCountryIsoCodes.push(parentCountryIso);
			}
		}
		return dedupeIsoStringsForMerge(parentCountryIsoCodes);
	}

	function mergeIsoListForProgressiveLoads(
		viewportCountryIso: string | null,
		selectionParentCountryIsoCodes: string[]
	): string[] {
		const viewportPlusSelectionIsos: string[] = [];
		if (viewportCountryIso) {
			viewportPlusSelectionIsos.push(String(viewportCountryIso));
		}
		for (
			let selectionIsoIndex = 0;
			selectionIsoIndex < selectionParentCountryIsoCodes.length;
			selectionIsoIndex++
		) {
			viewportPlusSelectionIsos.push(selectionParentCountryIsoCodes[selectionIsoIndex]);
		}
		return dedupeIsoStringsForMerge(viewportPlusSelectionIsos);
	}

	function filterIsosWithLocalGeoBoundariesData(isoCandidates: string[]): string[] {
		const isosWithBundledGeoJson: string[] = [];
		for (let candidateIndex = 0; candidateIndex < isoCandidates.length; candidateIndex++) {
			const countryIso = isoCandidates[candidateIndex];
			if (countryIso && hasLocalGeoBoundariesData(countryIso)) {
				isosWithBundledGeoJson.push(countryIso);
			}
		}
		return isosWithBundledGeoJson;
	}

	async function loadAdm1MergedForIsos(isos: string[]) {
		const countryIsosWithBundledData = filterIsosWithLocalGeoBoundariesData(isos);
		if (countryIsosWithBundledData.length === 0) {
			lastSuccessfulAdm1MergeKey = null;
			if (!disposed) {
				map.getSource(SOURCE_ADM1).setData(emptyFeatureCollection());
			}
			return;
		}
		const sortedCountryIsosForMergeKey = countryIsosWithBundledData.slice().sort();
		const mergeKey = sortedCountryIsosForMergeKey.join('|');
		if (mergeKey === lastSuccessfulAdm1MergeKey) {
			return;
		}
		if (loadingAdm1Key === mergeKey || disposed) {
			return;
		}
		loadingAdm1Key = mergeKey;
		const fetchGeneration = ++adm1FetchGeneration;
		notifyLoading(true, 'ADM1');
		try {
			// `hasLocalGeoBoundariesData` only validates the ISO shape, not file existence — some
			// countries (e.g. Antarctica `ATA`) simply have no ADM1 subdivisions on the CDN, so the
			// fetch 404s. With `Promise.all` one failure rejected the whole merge and the catch
			// below wiped SOURCE_ADM1, leaving the US (or any sibling in the merge) with no state
			// borders until the user unselected the problem country. `allSettled` keeps the working
			// countries and logs the misses.
			const perCountryAdm1Results = await Promise.allSettled(
				countryIsosWithBundledData.map((countryIso) =>
					fetchLocalGeoBoundariesGeoJson(countryIso, 'ADM1')
				)
			);
			const mergedAdm1Features: any[] = [];
			for (
				let countryFileIndex = 0;
				countryFileIndex < perCountryAdm1Results.length;
				countryFileIndex++
			) {
				const settledResult = perCountryAdm1Results[countryFileIndex];
				if (settledResult.status !== 'fulfilled') {
					console.warn(
						'local ADM1 load failed for',
						countryIsosWithBundledData[countryFileIndex],
						settledResult.reason
					);
					continue;
				}
				const adm1FeaturesOneCountry = normalizeFeatures(settledResult.value);
				for (let featureIndex = 0; featureIndex < adm1FeaturesOneCountry.length; featureIndex++) {
					mergedAdm1Features.push(adm1FeaturesOneCountry[featureIndex]);
				}
			}
			if (!disposed && fetchGeneration === adm1FetchGeneration) {
				lastSuccessfulAdm1MergeKey = mergeKey;
				map
					.getSource(SOURCE_ADM1)
					.setData(
						toMapboxFeatureCollection({ type: 'FeatureCollection', features: mergedAdm1Features })
					);
				scheduleRefresh();
			}
		} catch (err) {
			console.warn('local ADM1 merge load failed', mergeKey, err);
			if (!disposed && fetchGeneration === adm1FetchGeneration) {
				lastSuccessfulAdm1MergeKey = null;
				map.getSource(SOURCE_ADM1).setData(emptyFeatureCollection());
			}
		} finally {
			loadingAdm1Key = null;
			notifyLoading(false, 'ADM1');
		}
	}

	async function loadAdm2MergedForIsos(isos: string[]) {
		const countryIsosWithBundledData = filterIsosWithLocalGeoBoundariesData(isos);
		if (countryIsosWithBundledData.length === 0) {
			lastSuccessfulAdm2MergeKey = null;
			if (!disposed) {
				map.getSource(SOURCE_ADM2).setData(emptyFeatureCollection());
			}
			return;
		}
		const sortedCountryIsosForMergeKey = countryIsosWithBundledData.slice().sort();
		const mergeKey = sortedCountryIsosForMergeKey.join('|');
		if (mergeKey === lastSuccessfulAdm2MergeKey) {
			return;
		}
		if (loadingAdm2Key === mergeKey || disposed) {
			return;
		}
		loadingAdm2Key = mergeKey;
		const fetchGeneration = ++adm2FetchGeneration;
		notifyLoading(true, 'ADM2');
		try {
			// See the matching comment in loadAdm1MergedForIsos: some bundled ISOs (Antarctica, some
			// micro-states) have no ADM2 file on the CDN. `allSettled` keeps the working siblings
			// instead of wiping SOURCE_ADM2 for the whole merge.
			const perCountryAdm2Results = await Promise.allSettled(
				countryIsosWithBundledData.map((countryIso) =>
					fetchLocalGeoBoundariesGeoJson(countryIso, 'ADM2')
				)
			);
			const mergedAdm2Features: any[] = [];
			for (
				let countryFileIndex = 0;
				countryFileIndex < perCountryAdm2Results.length;
				countryFileIndex++
			) {
				const settledResult = perCountryAdm2Results[countryFileIndex];
				if (settledResult.status !== 'fulfilled') {
					console.warn(
						'local ADM2 load failed for',
						countryIsosWithBundledData[countryFileIndex],
						settledResult.reason
					);
					continue;
				}
				const adm2FeaturesOneCountry = normalizeFeatures(settledResult.value);
				for (let featureIndex = 0; featureIndex < adm2FeaturesOneCountry.length; featureIndex++) {
					mergedAdm2Features.push(adm2FeaturesOneCountry[featureIndex]);
				}
			}
			if (!disposed && fetchGeneration === adm2FetchGeneration) {
				lastSuccessfulAdm2MergeKey = mergeKey;
				map
					.getSource(SOURCE_ADM2)
					.setData(
						toMapboxFeatureCollection({ type: 'FeatureCollection', features: mergedAdm2Features })
					);
			}
		} catch (err) {
			console.warn('local ADM2 merge load failed', mergeKey, err);
			if (!disposed && fetchGeneration === adm2FetchGeneration) {
				lastSuccessfulAdm2MergeKey = null;
				map.getSource(SOURCE_ADM2).setData(emptyFeatureCollection());
			}
		} finally {
			loadingAdm2Key = null;
			notifyLoading(false, 'ADM2');
		}
	}

	async function loadAdm3(_iso: string) {
		// Global rollout is ADM0–ADM2 only; ADM3 is not downloaded. Keep layer empty (locality mode uses ADM2 / search).
		lastSuccessfulAdm3Iso = null;
		if (!disposed) {
			map.getSource(SOURCE_ADM3).setData(emptyFeatureCollection());
		}
	}

	function hasAnySelectedDistrict(): boolean {
		if (!options.getServiceAreas) {
			return false;
		}
		const selectedServiceAreas = options.getServiceAreas();
		if (!selectedServiceAreas) {
			return false;
		}
		for (
			let districtCheckIndex = 0;
			districtCheckIndex < selectedServiceAreas.length;
			districtCheckIndex++
		) {
			if (selectedServiceAreas[districtCheckIndex].placeType === 'district') {
				return true;
			}
		}
		return false;
	}

	function refreshForView() {
		if (disposed) {
			return;
		}
		try {
			const viewZoomLevel = map.getZoom();
			loadLocalAdm0Countries();
			if (viewZoomLevel < ZOOM_ADM1) {
				// Below ZOOM_ADM1 the gray ADM1/ADM2 pick-target fills+lines self-hide via their own
				// `minzoom`, but the selection layers (LAYER_SEL_ADM1_*, LAYER_SEL_ADM2_*) have no minzoom
				// so selected states/counties stay visible at world zoom. They still read from SOURCE_ADM1
				// / SOURCE_ADM2, so we must keep those sources populated for any country with selections.
				const selectionParentIsosBelowAdm1 = filterIsosWithLocalGeoBoundariesData(
					collectCountryShapeGroupsForMergedAdminLayers()
				);
				if (selectionParentIsosBelowAdm1.length > 0) {
					activeCountryIso = selectionParentIsosBelowAdm1[0];
					loadAdm1MergedForIsos(selectionParentIsosBelowAdm1);
					if (hasAnySelectedDistrict()) {
						loadAdm2MergedForIsos(selectionParentIsosBelowAdm1);
					} else if (!disposed) {
						map.getSource(SOURCE_ADM2).setData(emptyFeatureCollection());
						lastSuccessfulAdm2MergeKey = null;
						adm2FetchGeneration++;
					}
					if (!disposed) {
						map.getSource(SOURCE_ADM3).setData(emptyFeatureCollection());
					}
					lastSuccessfulAdm3Iso = null;
				} else {
					if (!disposed) {
						map.getSource(SOURCE_ADM1).setData(emptyFeatureCollection());
						map.getSource(SOURCE_ADM2).setData(emptyFeatureCollection());
						map.getSource(SOURCE_ADM3).setData(emptyFeatureCollection());
					}
					activeCountryIso = null;
					lastSuccessfulAdm1MergeKey = null;
					lastSuccessfulAdm2MergeKey = null;
					lastSuccessfulAdm3Iso = null;
					adm1FetchGeneration++;
					adm2FetchGeneration++;
				}
				return;
			}
			const viewportCountryIso = resolveActiveCountryIso();
			const countryIsoCodesForAdminMerge = mergeIsoListForProgressiveLoads(
				viewportCountryIso,
				collectCountryShapeGroupsForMergedAdminLayers()
			);
			const countryIsoCodesWithBundledData = filterIsosWithLocalGeoBoundariesData(
				countryIsoCodesForAdminMerge
			);
			if (countryIsoCodesWithBundledData.length > 0) {
				activeCountryIso = viewportCountryIso
					? viewportCountryIso
					: countryIsoCodesWithBundledData[0];
				loadAdm1MergedForIsos(countryIsoCodesForAdminMerge);
			} else {
				activeCountryIso = null;
				lastSuccessfulAdm1MergeKey = null;
				lastSuccessfulAdm2MergeKey = null;
				lastSuccessfulAdm3Iso = null;
				adm1FetchGeneration++;
				adm2FetchGeneration++;
				if (!disposed) {
					map.getSource(SOURCE_ADM1).setData(emptyFeatureCollection());
					map.getSource(SOURCE_ADM2).setData(emptyFeatureCollection());
					map.getSource(SOURCE_ADM3).setData(emptyFeatureCollection());
				}
			}
			if (viewZoomLevel < ZOOM_ADM2) {
				// Below ZOOM_ADM2 the gray ADM2 fill+line self-hide, but LAYER_SEL_ADM2_* has no minzoom
				// and still needs SOURCE_ADM2 populated for any country with a selected county.
				if (hasAnySelectedDistrict() && countryIsoCodesWithBundledData.length > 0) {
					loadAdm2MergedForIsos(countryIsoCodesForAdminMerge);
				} else if (!disposed) {
					map.getSource(SOURCE_ADM2).setData(emptyFeatureCollection());
					lastSuccessfulAdm2MergeKey = null;
					adm2FetchGeneration++;
				}
				if (!disposed) {
					map.getSource(SOURCE_ADM3).setData(emptyFeatureCollection());
				}
				lastSuccessfulAdm3Iso = null;
				return;
			}
			if (countryIsoCodesWithBundledData.length > 0) {
				loadAdm2MergedForIsos(countryIsoCodesForAdminMerge);
			}
			if (viewZoomLevel < ZOOM_ADM3) {
				map.getSource(SOURCE_ADM3).setData(emptyFeatureCollection());
				lastSuccessfulAdm3Iso = null;
				return;
			}
			if (activeCountryIso) {
				loadAdm3(activeCountryIso);
			}
		} finally {
			if (!disposed) {
				applyReferenceAdminLineVisibilityForFormMode();
			}
		}
	}

	function scheduleRefresh() {
		if (debounceTimer !== null) {
			window.clearTimeout(debounceTimer);
		}
		debounceTimer = window.setTimeout(() => {
			debounceTimer = null;
			refreshForView();
		}, 280);
	}

	map.on('moveend', scheduleRefresh);
	map.on('zoomend', scheduleRefresh);
	// Separate listener so the pill reflects zoom level changes even when the user never moves the
	// mouse (e.g. +/− buttons, programmatic fitBounds). Runs on zoomend for a stable post-animation read.
	map.on('zoomend', updateBoundaryZoomHintPill);
	if (isInteractive) {
		map.on('mousemove', onMouseMove);
		map.on('mouseout', onMouseLeave);
		map.on('click', onClick);
	}

	// Always schedule: this module is attached from ServiceAreaMap's `load` handler, so `once('load')`
	// would never run again and skipping here left refreshForView (ADM1/ADM2) never called.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(map as any)._owApplyBoundaryReferenceLineOpacity = applyReferenceAdminLineVisibilityForFormMode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(map as any)._owRefreshProgressiveBoundaryTooltipFromLastPointer =
		refreshBoundaryHoverTooltipFromLastPointer;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(map as any)._owScheduleProgressiveRefresh = scheduleRefresh;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(map as any)._owUpdateBoundaryZoomHint = updateBoundaryZoomHintPill;
	applyReferenceAdminLineVisibilityForFormMode();
	scheduleRefresh();
	updateBoundaryZoomHintPill();

	return () => {
		restoreMapboxBasemapAdminBoundaryLineVisibility();
		disposed = true;
		hideBoundaryHoverTooltip();
		if (boundaryHoverTooltipEl.parentNode) {
			boundaryHoverTooltipEl.parentNode.removeChild(boundaryHoverTooltipEl);
		}
		if (boundaryZoomHintPillEl.parentNode) {
			boundaryZoomHintPillEl.parentNode.removeChild(boundaryZoomHintPillEl);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		delete (map as any)._owApplyBoundaryReferenceLineOpacity;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		delete (map as any)._owRefreshProgressiveBoundaryTooltipFromLastPointer;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		delete (map as any)._owScheduleProgressiveRefresh;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		delete (map as any)._owUpdateBoundaryZoomHint;
		if (debounceTimer !== null) {
			window.clearTimeout(debounceTimer);
		}
		map.off('moveend', scheduleRefresh);
		map.off('zoomend', scheduleRefresh);
		map.off('zoomend', updateBoundaryZoomHintPill);
		if (isInteractive) {
			map.off('mousemove', onMouseMove);
			map.off('mouseout', onMouseLeave);
			map.off('click', onClick);
		}
		const layerIds = [
			LAYER_SEARCH_PREVIEW_LINE,
			LAYER_STORED_LINE,
			LAYER_STORED_FILL,
			LAYER_SEL_MARKERS_DISTRICT,
			LAYER_SEL_MARKERS_REGION,
			LAYER_HOVER_ADM3,
			LAYER_HOVER_ADM2,
			LAYER_HOVER_ADM1,
			LAYER_HOVER_ADM0,
			LAYER_SEL_ADM3_LINE,
			LAYER_SEL_ADM3_FILL,
			LAYER_SEL_ADM2_LINE,
			LAYER_SEL_ADM2_FILL,
			LAYER_SEL_ADM1_LINE,
			LAYER_SEL_ADM1_FILL,
			LAYER_SEL_ADM0_LINE,
			LAYER_SEL_ADM0_FILL,
			LAYER_ADM3_LINE,
			LAYER_ADM3_FILL,
			LAYER_ADM2_LINE,
			LAYER_ADM2_FILL,
			LAYER_ADM1_LINE,
			LAYER_ADM1_FILL,
			LAYER_ADM0_LINE,
			LAYER_ADM0_FILL,
		];
		for (let layerRemovalIndex = 0; layerRemovalIndex < layerIds.length; layerRemovalIndex++) {
			if (map.getLayer(layerIds[layerRemovalIndex])) {
				map.removeLayer(layerIds[layerRemovalIndex]);
			}
		}
		const mapSourceIdsToRemove = [
			SOURCE_SEARCH_PREVIEW,
			SOURCE_STORED,
			SOURCE_SEL_MARKERS,
			SOURCE_ADM3,
			SOURCE_ADM2,
			SOURCE_ADM1,
			SOURCE_ADM0,
		];
		for (
			let sourceRemovalIndex = 0;
			sourceRemovalIndex < mapSourceIdsToRemove.length;
			sourceRemovalIndex++
		) {
			if (map.getSource(mapSourceIdsToRemove[sourceRemovalIndex])) {
				map.removeSource(mapSourceIdsToRemove[sourceRemovalIndex]);
			}
		}
	};
}
